import React from 'react';

import { Col, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';

import { CustomerRowData, Milestone } from '../helpers/customerRowData';
import { formatPhoneNumberInput } from '../helpers/formatUtils';
import StatusBadge, { BadgeColor } from './StatusBadge';

interface ICustomerRowProps {
  data: CustomerRowData;
}

const MILESTONE_BADGE_COLOR: Record<Milestone, BadgeColor> = {
  [Milestone.ATTENTION]: 'red',
  [Milestone.REQUESTED]: 'green',
  [Milestone.OFFER_SENT]: 'green',
  [Milestone.IN_ASSESSMENTS]: 'green',
  [Milestone.IN_CONTRACT]: 'green',
  [Milestone.CLOSED]: 'green',
};

const relativeTime = (date: Date | null): string | null => {
  if (!date) return null;
  const diffMs = Date.now() - date.getTime();
  const day = 1000 * 60 * 60 * 24;
  const days = Math.floor(diffMs / day);
  if (days < 1) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
};

const CustomerRow: React.FC<ICustomerRowProps> = ({ data }) => {
  const relative = relativeTime(data.lastUpdate);
  return (
    <Col>
      <Row className="items-start justify-between pb-0.5">
        <Col className="flex-1 pr-2">
          <Text as="p" className="text-body-lg font-medium">
            {data.fullName || 'Unnamed customer'}
          </Text>
          <Text as="p" className="text-body-md text-od-content-secondary">
            {data.street}
          </Text>
        </Col>
        <Col className="items-end pt-0.5">
          <StatusBadge status={data.subStatus} color={MILESTONE_BADGE_COLOR[data.milestone]} />
          {relative ? (
            <Text as="span" className="text-body-sm pt-0.5 text-od-content-secondary">
              {relative}
            </Text>
          ) : null}
        </Col>
      </Row>
      {data.email || data.phoneNumber ? (
        <Row>
          {data.email ? (
            <Text as="span" className="text-body-sm text-od-content-secondary">
              {data.email}
            </Text>
          ) : null}
          {data.email && data.phoneNumber ? (
            <Text as="span" className="text-body-sm px-2 text-od-content-secondary">
              •
            </Text>
          ) : null}
          {data.phoneNumber ? (
            <Text as="span" className="text-body-sm text-od-content-secondary">
              {formatPhoneNumberInput(data.phoneNumber)}
            </Text>
          ) : null}
        </Row>
      ) : null}
    </Col>
  );
};

export default CustomerRow;
